import React, { useState } from 'react';
import {
    Table,
    Button,
    Form,
    Input,
    InputNumber,
    Card,
    Typography,
    Tag,
    Row,
    Col,
    Flex,
    Space,
    Modal,
    message,
    Spin,
    Popconfirm,
    Switch
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, StarFilled } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { usePackagePlans } from '../../hooks/usePackagePlans';
import { addPackagePlan, updatePackagePlan, deletePackagePlan } from '../../store/slices/packagePlansSlice';

const { Title } = Typography;

const Packages = () => {
    const dispatch = useDispatch();
    const selectedBranch = useSelector((state) => state.branches.selectedBranch);
    const selectedPlace = useSelector((state) => state.branches.selectedPlace);
    const { packagePlans, loading, refetchPackagePlans } = usePackagePlans(selectedBranch?.id, selectedPlace?.id);
    
    const [showAddForm, setShowAddForm] = useState(false);
    const [editingPackagePlan, setEditingPackagePlan] = useState(null);
    const [form] = Form.useForm();

    const handleAddPackagePlan = async (values) => {
        try {
            const monthlyPrice = values.price / values.durationMonth;
            const packagePlanData = {
                name: values.name,
                durationMonth: values.durationMonth,
                price: values.price,
                monthlyPrice: monthlyPrice,
                isPopular: values.isPopular || false,
                branchId: selectedBranch.id,
                placeId: selectedPlace?.id
            };

            await dispatch(addPackagePlan(packagePlanData)).unwrap();
            message.success('Paket başarıyla eklendi!');
            refetchPackagePlans();
            form.resetFields();
            setShowAddForm(false);
        } catch (error) {
            console.error('Add package plan error:', error);
            message.error(`Paket eklenirken bir hata oluştu: ${error.message || error}`);
        }
    };

    const handleEditPackagePlan = async (values) => {
        try {
            const monthlyPrice = values.price / values.durationMonth;
            const packagePlanData = {
                id: editingPackagePlan.id,
                name: values.name,
                durationMonth: values.durationMonth,
                price: values.price,
                monthlyPrice: monthlyPrice,
                isPopular: values.isPopular || false,
                branchId: selectedBranch.id,
                placeId: selectedPlace?.id
            };

            await dispatch(updatePackagePlan(packagePlanData)).unwrap();
            message.success('Paket başarıyla güncellendi!');
            refetchPackagePlans();
            form.resetFields();
            setShowAddForm(false);
            setEditingPackagePlan(null);
        } catch (error) {
            console.error('Update package plan error:', error);
            message.error(`Paket güncellenirken bir hata oluştu: ${error.message || error}`);
        }
    };

    const handleDeletePackagePlan = async (id) => {
        try {
            await dispatch(deletePackagePlan(id)).unwrap();
            message.success('Paket başarıyla silindi!');
            refetchPackagePlans();
        } catch (error) {
            console.error('Delete package plan error:', error);
            message.error(`Paket silinirken bir hata oluştu: ${error.message || error}`);
        }
    };

    const showEditModal = (packagePlan) => {
        setEditingPackagePlan(packagePlan);
        form.setFieldsValue({
            name: packagePlan.name,
            durationMonth: packagePlan.durationMonth,
            price: packagePlan.price,
            monthlyPrice: packagePlan.monthlyPrice,
            isPopular: packagePlan.isPopular
        });
        setShowAddForm(true);
    };

    const handleCancel = () => {
        setShowAddForm(false);
        setEditingPackagePlan(null);
        form.resetFields();
    };

    const columns = [
        {
            title: 'Paket Adı',
            dataIndex: 'name',
            key: 'name',
            fixed: 'left',
            render: (name, record) => (
                <Space>
                    {name}
                    {record.isPopular && <StarFilled style={{ color: '#faad14' }} />}
                </Space>
            ),
        },
        {
            title: 'Süre (Ay)',
            dataIndex: 'durationMonth',
            key: 'durationMonth',
            render: (duration) => `${duration} ay`,
        },
        {
            title: 'Fiyat (TL)',
            dataIndex: 'price',
            key: 'price',
            render: (price) => `${price.toLocaleString('tr-TR')} TL`,
        },
        {
            title: 'Aylık Fiyat (TL)',
            dataIndex: 'monthlyPrice',
            key: 'monthlyPrice',
            render: (monthlyPrice) => `${monthlyPrice.toLocaleString('tr-TR')} TL`,
        },

        {
            title: 'Popüler',
            dataIndex: 'isPopular',
            key: 'isPopular',
            render: (isPopular) => (
                <Tag color={isPopular ? 'gold' : 'default'}>
                    {isPopular ? 'Evet' : 'Hayır'}
                </Tag>
            ),
        },
        {
            title: 'İşlemler',
            key: 'actions',
            fixed: 'right',
            width: 120,
            render: (_, record) => (
                <Space>
                    <Button
                        type="primary"
                        size="small"
                        icon={<EditOutlined />}
                        onClick={() => showEditModal(record)}
                    />
                    <Popconfirm
                        title="Bu paketi silmek istediğinizden emin misiniz?"
                        onConfirm={() => handleDeletePackagePlan(record.id)}
                        okText="Evet"
                        cancelText="Hayır"
                    >
                        <Button
                            type="primary"
                            danger
                            size="small"
                            icon={<DeleteOutlined />}
                        />
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    // Eğer seçili branch yoksa uyarı göster
    if (!selectedBranch) {
        return (
            <Flex vertical gap="middle" style={{ padding: '16px' }}>
                <div style={{ textAlign: 'center', padding: '40px' }}>
                    <h3>Lütfen önce bir branş seçiniz</h3>
                    <p>Paketleri yönetmek için önce spor branşı sayfasından bir branş seçmelisiniz.</p>
                </div>
            </Flex>
        );
    }

    return (
        <Flex vertical gap="middle" style={{ padding: '16px' }}>
            <Flex justify="space-between" align="center">
                <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => setShowAddForm(true)}
                    loading={loading}
                >
                    Yeni Paket Ekle
                </Button>
            </Flex>

            <Modal
                title={editingPackagePlan ? "Paket Düzenle" : "Yeni Paket Ekle"}
                open={showAddForm}
                onCancel={handleCancel}
                footer={null}
                width={800}
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={editingPackagePlan ? handleEditPackagePlan : handleAddPackagePlan}
                >
                    <Row gutter={16}>
                        <Col xs={24} sm={12}>
                            <Form.Item
                                name="name"
                                label="Paket Adı"
                                rules={[{ required: true, message: 'Lütfen paket adı girin!' }]}
                            >
                                <Input />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                            <Form.Item
                                name="durationMonth"
                                label="Süre (Ay)"
                                rules={[{ required: true, message: 'Lütfen süre girin!' }]}
                            >
                                <InputNumber 
                                    style={{ width: '100%' }} 
                                    min={1} 
                                    max={60} 
                                    onChange={(value) => {
                                        const price = form.getFieldValue('price');
                                        if (value && price) {
                                            const monthlyPrice = price / value;
                                            form.setFieldValue('monthlyPrice', monthlyPrice);
                                        }
                                    }}
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col xs={24} sm={12}>
                            <Form.Item
                                name="price"
                                label="Fiyat (TL)"
                                rules={[{ required: true, message: 'Lütfen fiyat girin!' }]}
                            >
                                <InputNumber 
                                    style={{ width: '100%' }} 
                                    min={0} 
                                    formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                    parser={value => value.replace(/\$\s?|(,*)/g, '')}
                                    onChange={(value) => {
                                        const durationMonth = form.getFieldValue('durationMonth');
                                        if (value && durationMonth) {
                                            const monthlyPrice = value / durationMonth;
                                            form.setFieldValue('monthlyPrice', monthlyPrice);
                                        }
                                    }}
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                            <Form.Item
                                name="monthlyPrice"
                                label="Aylık Fiyat (TL)"
                                tooltip="Otomatik hesaplanır (Fiyat / Süre)"
                            >
                                <InputNumber 
                                    style={{ width: '100%' }} 
                                    min={0} 
                                    disabled
                                    formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                    parser={value => value.replace(/\$\s?|(,*)/g, '')}
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col xs={24}>
                            <Form.Item
                                name="isPopular"
                                label="Popüler Paket"
                                valuePropName="checked"
                            >
                                <Switch />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item>
                        <Space>
                            <Button type="primary" htmlType="submit" loading={loading}>
                                {editingPackagePlan ? 'Güncelle' : 'Kaydet'}
                            </Button>
                            <Button onClick={handleCancel}>
                                İptal
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>

            <Table
                columns={columns}
                dataSource={packagePlans}
                rowKey="id"
                pagination={{
                    pageSize: 10,
                    showSizeChanger: true,
                    showQuickJumper: true,
                    showTotal: (total, range) => `${range[0]}-${range[1]} / ${total} paket`
                }}
                scroll={{ x: true }}
                loading={loading}
                locale={{
                    emptyText: loading ? <Spin /> : 'Henüz paket bulunmamaktadır'
                }}
            />
        </Flex>
    );
};

export default Packages; 